/**
 * Contexto para histórico de visualizações de produtos
 * 
 * Armazena os produtos visualizados pelo usuário para:
 * - Sugestões de produtos relacionados
 * - Histórico de navegação
 * - Recomendações personalizadas
 */

import React, { createContext, useState, useContext, useEffect } from 'react';

const ViewHistoryContext = createContext();

const STORAGE_KEY = 'emperc_view_history';
const MAX_HISTORY_ITEMS = 50; // Limitar histórico a 50 itens

// Carregar histórico do localStorage
const loadHistoryFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Erro ao carregar histórico do localStorage:', error);
    return [];
  }
};

// Salvar histórico no localStorage
const saveHistoryToStorage = (history) => {
  try {
    // Manter apenas os últimos MAX_HISTORY_ITEMS
    const limitedHistory = history.slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedHistory));
  } catch (error) {
    console.error('Erro ao salvar histórico no localStorage:', error);
  }
};

export const ViewHistoryProvider = ({ children }) => {
  const [viewHistory, setViewHistory] = useState(() => loadHistoryFromStorage());

  // Sincronizar com localStorage sempre que histórico mudar
  useEffect(() => {
    saveHistoryToStorage(viewHistory);
  }, [viewHistory]);

  /**
   * Adiciona um produto ao histórico de visualizações
   */
  const addToHistory = (product) => {
    if (!product || (!product.id && !product._id)) {
      return;
    }

    const productId = product.id || product._id;

    setViewHistory((prevHistory) => {
      // Remover se já existir (evitar duplicatas)
      const filtered = prevHistory.filter(
        (item) => (item.id || item._id) !== productId
      );

      // Normalizar produto
      const normalizedProduct = {
        ...product,
        id: product.id || product._id,
        _id: product._id || product.id,
        viewedAt: new Date().toISOString(),
      };

      // Adicionar no início (mais recente primeiro)
      return [normalizedProduct, ...filtered];
    });
  };

  /**
   * Remove um produto do histórico
   */
  const removeFromHistory = (productId) => {
    setViewHistory((prevHistory) =>
      prevHistory.filter(
        (item) => (item.id || item._id) !== productId
      )
    );
  };

  /**
   * Limpa todo o histórico
   */
  const clearHistory = () => {
    setViewHistory([]);
  };

  /**
   * Verifica se um produto está no histórico
   */
  const isInHistory = (productId) => {
    return viewHistory.some(
      (item) => (item.id || item._id) === productId
    );
  };

  /**
   * Obtém produtos relacionados baseado no histórico
   * Retorna produtos da mesma categoria ou marca
   */
  const getRelatedProducts = (currentProduct, allProducts = [], limit = 4) => {
    if (!currentProduct || viewHistory.length === 0) {
      return [];
    }

    const currentCategory = currentProduct.category?.name || currentProduct.category;
    const currentBrand = currentProduct.brand;

    // Filtrar produtos relacionados
    const related = allProducts
      .filter((product) => {
        const productId = product.id || product._id;
        const currentId = currentProduct.id || currentProduct._id;
        
        // Não incluir o produto atual
        if (productId === currentId) return false;

        // Priorizar mesma categoria
        const sameCategory = 
          (product.category?.name || product.category) === currentCategory;
        
        // Priorizar mesma marca
        const sameBrand = product.brand === currentBrand;

        return sameCategory || sameBrand;
      })
      .slice(0, limit);

    return related;
  };

  /**
   * Obtém produtos visualizados recentemente
   */
  const getRecentViews = (limit = 10) => {
    return viewHistory.slice(0, limit);
  };

  return (
    <ViewHistoryContext.Provider
      value={{
        viewHistory,
        addToHistory,
        removeFromHistory,
        clearHistory,
        isInHistory,
        getRelatedProducts,
        getRecentViews,
        historyCount: viewHistory.length,
      }}
    >
      {children}
    </ViewHistoryContext.Provider>
  );
};

export const useViewHistory = () => {
  const context = useContext(ViewHistoryContext);
  if (!context) {
    throw new Error('useViewHistory deve ser usado dentro de ViewHistoryProvider');
  }
  return context;
};

export default ViewHistoryProvider;

